import {
  ErrorCode,
  schedulerInterventionLockedReasons,
  schedulerNotInWarrantyReasons,
  schedulerSimulationInvalidReasons,
  type SchedulerSimulationInvalidReason,
} from '@kreart/shared';
import { ApiError } from '@/lib/api';
import {
  explainError,
  failedBecause,
  makeErrorKeyResolver,
  type ErrorMessage,
  type ErrorResolver,
} from '@/lib/form/errorMessages';
import { formatDate } from './components/scheduling-form';
import { formatCalendarDay } from './rescue';

const base = makeErrorKeyResolver({
  [ErrorCode.SCHEDULER_TEAM_BUSY]: 'operationsScheduler.workers.deleteTeamBusy',
  [ErrorCode.SCHEDULER_NO_AVAILABLE_TEAM]: 'operationsScheduler.startNow.noAvailableTeam',
  [ErrorCode.SCHEDULER_TEAM_CHOICE_CHANGED]: 'operationsScheduler.teamChoice.changed',
  [ErrorCode.SCHEDULER_PROJECT_FINISHED]: 'operationsScheduler.warranty.errors.projectFinished',
  [ErrorCode.SCHEDULER_INTERVENTION_OPEN]: 'operationsScheduler.warranty.errors.interventionOpen',
  [ErrorCode.SCHEDULER_TEAM_RESERVED]: 'operationsScheduler.rescue.teamReserved',
  [ErrorCode.SCHEDULER_LEAVE_OVERLAP]: 'operationsScheduler.workers.leave.overlap',
  [ErrorCode.SCHEDULER_LEAVE_ENDED]: 'operationsScheduler.workers.leave.ended',
});

/** `reason` when it is one the UI has words for; otherwise the given fallback. */
function knownReason<TReason extends string>(
  details: { reason?: unknown } | undefined,
  known: readonly TReason[],
  fallback: TReason,
): TReason {
  const reason = details?.reason;
  return known.includes(reason as TReason) ? (reason as TReason) : fallback;
}

/**
 * Scheduler refusals, worded as the reason they happened.
 *
 * Deliberately the same strings the Echipe pane of Pontaj lunar shows on its
 * disabled delete button: whether the blocker is spotted before the click or
 * only after the server refuses, the explanation — and the way out of it —
 * should read identically.
 */
export const errorKeyFor: ErrorResolver = (err: unknown): ErrorMessage => {
  if (err instanceof ApiError && err.code === ErrorCode.SCHEDULER_SIMULATION_INVALID) {
    // Why a simulated project could not be projected is only known to the
    // server (it holds the rates and shares), so the reason travels in details.
    const details = err.details as { reason?: string; maxHours?: number } | undefined;
    const reason = details?.reason as SchedulerSimulationInvalidReason | undefined;
    if (reason && schedulerSimulationInvalidReasons.includes(reason)) {
      return {
        key: `operationsScheduler.gantt.simulate.errors.${reason}`,
        values: { max: details?.maxHours ?? 0 },
      };
    }
    // A reason this build has no words for: the server's own, after what failed.
    return failedBecause('operationsScheduler.gantt.simulate.errors.failed', err, explainError);
  }
  if (err instanceof ApiError && err.code === ErrorCode.SCHEDULER_RESCUE_REJECTED) {
    // A refused recommendation almost always means the board moved on since it
    // was worked out — the generic "things changed" sentence. The one refusal
    // that is the manager's own to fix, and would read wrong as "things
    // changed", is a new collaborator's name already worn by another crew.
    const problems = (err.details as { problems?: Array<{ code?: unknown }> } | undefined)
      ?.problems;
    if (
      Array.isArray(problems) &&
      problems.length > 0 &&
      problems.every((problem) => problem?.code === 'NAME_TAKEN')
    ) {
      return 'operationsScheduler.rescue.nameTaken';
    }
    return 'operationsScheduler.rescue.rejected';
  }
  if (err instanceof ApiError && err.code === ErrorCode.SCHEDULER_LEAVE_OVERLAP) {
    // Name the leave it clashes with: it may be one the list no longer shows
    // (only leave still to come or running is listed).
    const details = err.details as { startDate?: unknown; endDate?: unknown } | undefined;
    if (typeof details?.startDate === 'string' && typeof details.endDate === 'string') {
      return {
        key: 'operationsScheduler.workers.leave.overlapWith',
        values: {
          from: formatCalendarDay(details.startDate),
          to: formatCalendarDay(details.endDate),
        },
      };
    }
  }
  if (err instanceof ApiError && err.code === ErrorCode.SCHEDULER_TEAM_HAS_WORKERS) {
    // The count is the whole point of this message, and only the server knows
    // it: the board's badge counts active members, while any roster row at all
    // blocks the delete.
    const workers = (err.details as { workers?: number } | undefined)?.workers ?? 0;
    return { key: 'operationsScheduler.workers.deleteTeamHasWorkers', values: { count: workers } };
  }
  if (err instanceof ApiError && err.code === ErrorCode.SCHEDULER_WARRANTY_EXPIRED) {
    // Expiry is measured on the scheduler's virtual clock, which the browser
    // does not have — so the end date is the server's, never recomputed here.
    const details = err.details as { warrantyEndsAt?: string } | undefined;
    return {
      key: 'operationsScheduler.warranty.errors.expired',
      values: { date: formatDate(details?.warrantyEndsAt) },
    };
  }
  if (err instanceof ApiError && err.code === ErrorCode.SCHEDULER_NOT_IN_WARRANTY) {
    const details = err.details as { reason?: unknown } | undefined;
    const reason = knownReason(details, schedulerNotInWarrantyReasons, 'NOT_COMPLETED');
    return `operationsScheduler.warranty.errors.notInWarranty.${reason}`;
  }
  if (err instanceof ApiError && err.code === ErrorCode.SCHEDULER_INTERVENTION_LOCKED) {
    // One code for every "not on an intervention" refusal; the reason picks the
    // sentence, because each one has a different way out (cancel and reopen,
    // use cancel instead of remove, pick an earlier start…).
    const details = err.details as { reason?: unknown; warrantyEndsAt?: string } | undefined;
    const reason = knownReason(details, schedulerInterventionLockedReasons, 'NOT_INTERVENTION');
    const key = `operationsScheduler.warranty.errors.locked.${reason}`;
    return details?.warrantyEndsAt
      ? { key, values: { date: formatDate(details.warrantyEndsAt) } }
      : key;
  }
  return base(err);
};
